import { Button, Card, CardContent, Input, Label, Textarea } from "@repo/ui";
import { useMemo, useState } from "react";

type Lang = "th" | "en";

const copy = {
  th: {
    nav: { lang: "ภาษา", book: "จองคิว" },
    heroTitle: "Kami Kami — ซาลอนหรูในกรุงเทพฯ",
    heroSubtitle: "ผมสวย เงางาม ดีไซน์เฉพาะคุณ ดูแลโดยสไตลิสต์มืออาชีพ",
    bookCta: "จองคิวตอนนี้",
    servicesTitle: "บริการยอดนิยม",
    services: ["ตัดผม", "ทำสี", "ทรีตเมนต์เคราติน", "บราซิลเลียนบลโลว์เอาท์"],
    galleryTitle: "บรรยากาศ & ผลงาน",
    locationTitle: "ทำเล & เวลาเปิดทำการ",
    address: "สุขุมวิท • กรุงเทพฯ",
    hours: "เปิดทุกวัน 10:00–20:00 น.",
    bookingTitle: "จองคิว",
    form: {
      name: "ชื่อ",
      phone: "เบอร์โทร",
      service: "บริการ",
      date: "วันที่",
      time: "เวลา",
      contact: "ช่องทางติดต่อที่สะดวก",
      notes: "รายละเอียดเพิ่มเติม",
      submit: "ส่งคำขอจอง",
      success: "ส่งคำขอแล้ว เราจะติดต่อยืนยันโดยเร็วที่สุด",
    },
  },
  en: {
    nav: { lang: "Language", book: "Book" },
    heroTitle: "Kami Kami — Modern luxury salon in Bangkok",
    heroSubtitle: "Tailored cuts, luminous color, and premium care by expert stylists",
    bookCta: "Book now",
    servicesTitle: "Signature services",
    services: ["Precision cut", "Color & highlights", "Keratin treatment", "Brazilian blowout"],
    galleryTitle: "Space & work",
    locationTitle: "Location & hours",
    address: "Sukhumvit • Bangkok",
    hours: "Open daily 10:00–20:00",
    bookingTitle: "Book an appointment",
    form: {
      name: "Name",
      phone: "Phone",
      service: "Service",
      date: "Date",
      time: "Time",
      contact: "Preferred contact",
      notes: "Notes",
      submit: "Request booking",
      success: "Request sent. We'll confirm shortly.",
    },
  },
};

const servicesList = [
  "Precision Cut / ตัดผม",
  "Color & Highlights / ทำสี",
  "Keratin Treatment / ทรีตเมนต์เคราติน",
  "Brazilian Blowout / บราซิลเลียนบลโลว์เอาท์",
];

export function Home() {
  const [lang, setLang] = useState<Lang>("th");
  const t = useMemo(() => copy[lang], [lang]);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setError(null);
    const fd = new FormData(e.currentTarget);
    const payload = Object.fromEntries(fd.entries());
    try {
      const res = await fetch("/api/booking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed");
      setStatus("success");
      (e.currentTarget as HTMLFormElement).reset();
    } catch (err) {
      setStatus("error");
      setError((err as Error).message);
    }
  }

  return (
    <div className="min-h-dvh">
      {/* Top bar */}
      <header className="w-full border-b bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-accent grid place-items-center">
              <span className="font-semibold" style={{ fontFamily: "var(--font-display)" }}>
                K
              </span>
            </div>
            <span className="text-lg" style={{ fontFamily: "var(--font-display)" }}>
              Kami Kami
            </span>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value as Lang)}
              className="rounded-md border bg-background px-3 py-2"
            >
              <option value="th">TH</option>
              <option value="en">EN</option>
            </select>
            <a href="#booking">
              <Button>{t.nav.book}</Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.pexels.com/photos/10317444/pexels-photo-10317444.jpeg"
            alt="Golden salon mirror"
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 to-background/95" />
        </div>
        <div className="mx-auto max-w-6xl px-6 py-24 md:py-32">
          <h1 className="text-4xl md:text-6xl leading-tight">{t.heroTitle}</h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{t.heroSubtitle}</p>
          <div className="mt-8">
            <a href="#booking">
              <Button size="lg">{t.bookCta}</Button>
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="text-2xl md:text-3xl mb-8">{t.servicesTitle}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {t.services.map((s) => (
            <Card key={s} className="border bg-card/60">
              <CardContent className="p-6 text-center">
                <div className="text-lg">{s}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="text-2xl md:text-3xl mb-8">{t.galleryTitle}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <img
            className="rounded-lg object-cover h-64 w-full"
            src="https://images.pexels.com/photos/6186745/pexels-photo-6186745.jpeg"
            alt="Elegant gold accents"
          />
          <img
            className="rounded-lg object-cover h-64 w-full"
            src="https://images.pexels.com/photos/6188038/pexels-photo-6188038.jpeg"
            alt="Serene setting"
          />
          <img
            className="rounded-lg object-cover h-64 w-full"
            src="https://images.pexels.com/photos/4709523/pexels-photo-4709523.jpeg"
            alt="Marble texture"
          />
        </div>
      </section>

      {/* Location & hours */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="text-2xl md:text-3xl mb-4">{t.locationTitle}</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-lg">{t.address}</div>
              <div className="text-muted-foreground">BTS สุขุมวิท โซนพร้อมพงษ์–ทองหล่อ</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-lg">{t.hours}</div>
              <div className="text-muted-foreground">Walk-in welcome, แนะนำให้จองล่วงหน้า</div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Booking form */}
      <section id="booking" className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="text-2xl md:text-3xl mb-8">{t.bookingTitle}</h2>
        <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="name">{t.form.name}</Label>
            <Input id="name" name="name" required placeholder="" />
          </div>
          <div>
            <Label htmlFor="phone">{t.form.phone}</Label>
            <Input id="phone" name="phone" required placeholder="" />
          </div>
          <div>
            <Label htmlFor="service">{t.form.service}</Label>
            <select
              id="service"
              name="service"
              required
              className="mt-2 w-full rounded-md border bg-background px-3 py-2"
            >
              {servicesList.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">{t.form.date}</Label>
              <Input type="date" id="date" name="date" required />
            </div>
            <div>
              <Label htmlFor="time">{t.form.time}</Label>
              <Input type="time" id="time" name="time" required />
            </div>
          </div>
          <div>
            <Label htmlFor="preferred_contact">{t.form.contact}</Label>
            <Input id="preferred_contact" name="preferred_contact" placeholder="LINE / Phone" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="notes">{t.form.notes}</Label>
            <Textarea id="notes" name="notes" rows={4} />
          </div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={status === "submitting"}>
              {t.form.submit}
            </Button>
            {status === "success" && <span className="ml-3 text-green-700">{t.form.success}</span>}
            {status === "error" && <span className="ml-3 text-destructive">{error}</span>}
          </div>
        </form>
      </section>

      <footer className="border-t py-10 text-center text-sm text-muted-foreground">
        © {new Date().getFullYear()} Kami Kami — Bangkok
      </footer>
    </div>
  );
}
